import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Calculator } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const STATS = [
  { value: "500+", label: "Consignments Cleared", testid: "hero-stat-clearances" },
  { value: "06", label: "Practice Verticals", testid: "hero-stat-verticals" },
  { value: "24H", label: "Response Window", testid: "hero-stat-response" },
];

export default function Hero({ scrollTo }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden noise-overlay"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src="https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Container terminal aerial"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E17]/80 via-[#0A0E17]/70 to-[#0A0E17]" />
        <div className="absolute inset-0 grid-lines opacity-60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-40 pb-14">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse-dot" />
          <p className="font-mono2 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-amber-400">
            26°49'N 89°01'E — Birpara · Alipurduar Trade Gateway
          </p>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-8xl">
          <MaskedLine delay={0.25}>Trade without</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-[#F59E0B]">borders.</span>
          </MaskedLine>
          <MaskedLine delay={0.55}>
            <span className="outline-text">Compliance</span>
          </MaskedLine>
          <MaskedLine delay={0.7}>without chaos.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          SHIVIN INFOTECH is a full-spectrum trade house — customs clearance,
          freight forwarding, import–export consultancy, and every registration
          your business needs to move goods and ideas across India and beyond.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-enquire"
            onClick={() => scrollTo("#contact")}
            className="group flex items-center gap-3 bg-[#F59E0B] text-[#0A0E17] font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full hover:bg-white transition-colors duration-300"
          >
            Start an enquiry
            <ArrowDownRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-cta-calculator"
            onClick={() => scrollTo("#estimator")}
            className="flex items-center gap-3 border border-white/25 text-slate-100 font-mono2 text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors duration-300"
          >
            <Calculator size={16} /> Duty estimator
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-16 grid grid-cols-3 max-w-2xl border-t border-white/10"
        >
          {STATS.map((s) => (
            <div key={s.label} data-testid={s.testid} className="pt-6 pr-6">
              <p className="font-mono2 text-2xl sm:text-4xl font-semibold text-[#F59E0B]">{s.value}</p>
              <p className="mt-1 font-mono2 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-slate-400">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
